// BlockNote custom block: Commitment — FE-044.
//
// Visible label is "Commitment" (never "Task"). Materialization
// happens server-side: when a Playbook with this block type is
// applied to an Account / PlanYear, ``backend/playbooks/services.py
// ::materialize_commitment_blocks`` walks the saved BlockNote tree
// and inserts a Commitment per block. Keep these props in lockstep
// with the materializer's expected shape.
//
// The full editing UX (priority pickers, due-date anchor + months/
// days inputs, dependency picker) is FE-045. FE-044 ships the
// minimal renderer + the schema definition so a Playbook editor
// page can register the block today and apply_playbook (BE-046)
// can read the blocks_json without further round-tripping.

import { createContext, useContext } from "react";
import type React from "react";
import { createReactBlockSpec } from "@blocknote/react";
import { Copy, Pencil, Trash2 } from "lucide-react";
import {
  type CommitmentDueAnchor,
  type CommitmentDuePriority,
} from "@/features/playbooks/types";


// BlockNote prop schemas don't allow array / object props directly.
// We persist:
//
//  - Scalar fields verbatim.
//  - ``depends_on_block_uids`` as a comma-joined string ("a,b,c");
//    the materializer + the FE serializer split / join. An empty
//    string means "no dependencies".
//
// FE-045 adds the dependency-picker UX; the on-disk shape is
// already settled here so blocks authored in FE-044 round-trip.

const DUE_ANCHORS: readonly CommitmentDueAnchor[] = [
  "apply_date",
  "plan_year_start",
  "plan_year_end",
  "bor_date",
];

const PRIORITIES: readonly CommitmentDuePriority[] = ["high", "medium", "low"];


export const COMMITMENT_BLOCK_TYPE = "commitment" as const;

type Option = {
  id?: string;
  slug?: string;
  display_name: string;
  is_regulatory?: boolean;
};

type CommitmentBlockActions = {
  readOnly?: boolean;
  categories?: Option[];
  tags?: Option[];
  onEdit?: (blockId: string) => void;
  onDuplicate?: (blockId: string) => void;
  onDelete?: (blockId: string) => void;
};

const CommitmentBlockActionsContext = createContext<CommitmentBlockActions>({});

export function CommitmentBlockActionsProvider({
  value,
  children,
}: {
  value: CommitmentBlockActions;
  children: React.ReactNode;
}) {
  return (
    <CommitmentBlockActionsContext.Provider value={value}>
      {children}
    </CommitmentBlockActionsContext.Provider>
  );
}


/** The BlockNote propSchema for the Commitment block. The values
 *  flow through ``editor.document`` (BlockNote's serialization),
 *  ride into ``blocks_json``, and land in PostgreSQL via
 *  ``materialize_commitment_blocks``. Keys must match the backend
 *  materializer's recognized ``props`` keys. */
export const commitmentBlockPropSchema = {
  title: { default: "" as string },
  category_slug: { default: "service" as string },
  default_priority: {
    default: "low" as CommitmentDuePriority,
    values: PRIORITIES,
  },
  default_complexity: { default: 1 as number },
  due_anchor: {
    default: "" as "" | CommitmentDueAnchor,
    values: ["", ...DUE_ANCHORS] as const,
  },
  due_offset_months: { default: 0 as number },
  due_offset_days: { default: 0 as number },
  depends_on_block_uids: { default: "" as string },
  tag_ids: { default: "" as string },
  is_regulatory: { default: false as boolean },
  description: { default: "" as string },
  assigned_job_title: { default: "" as string },
} as const;


function dueRuleSummary(
  anchor: string, months: number, days: number,
): string {
  if (!anchor) return "No due date anchor set.";
  const labels: Record<string, string> = {
    apply_date: "Apply date",
    plan_year_start: "Plan year start",
    plan_year_end: "Plan year end",
    bor_date: "Broker of Record date",
  };
  const label = labels[anchor] ?? anchor;
  if (!months && !days) return `Due on ${label}.`;
  if (months && !days) return `Due ${months} ${plural(months, "month")} after ${label}.`;
  if (!months && days) return `Due ${days} ${plural(days, "day")} after ${label}.`;
  return `Due ${months} ${plural(months, "month")} and ${days} ${plural(days, "day")} after ${label}.`;
}


/** Block label visible in the editor + slash menu. Never use
 *  "Task" — that's v1 vocabulary. */
export const COMMITMENT_BLOCK_LABEL = "Commitment";


function CommitmentBlockView({
  block,
  contentRef,
}: {
  block: { id: string; props: Record<string, unknown> };
  contentRef: React.Ref<HTMLDivElement>;
}) {
  const actions = useContext(CommitmentBlockActionsContext);
  const props = block.props;
  const category = optionLabel(
    String(props.category_slug || "service"),
    actions.categories,
  );
  const priority = priorityLabel(String(props.default_priority || "low"));
  const complexity = complexityLabel(String(props.default_complexity || "1"));
  const due = dueRuleSummary(
    String(props.due_anchor || ""),
    Number(props.due_offset_months || 0),
    Number(props.due_offset_days || 0),
  );
  const tagIds = stringList(props.tag_ids);
  const tagLabels = tagIds.map((id) => optionLabel(id, actions.tags));
  const dependencyCount = stringList(props.depends_on_block_uids).length;
  const regulatory = Boolean(props.is_regulatory) ||
    tagIds.some((id) => actions.tags?.some((tag) =>
      tag.is_regulatory && (tag.id === id || tag.slug === id),
    ));

  return (
    <section
      data-testid={`playbook-commitment-block-${block.id}`}
      data-block-type={COMMITMENT_BLOCK_TYPE}
      className="my-2 rounded-md border border-border-subtle bg-raised p-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-teal">
            {COMMITMENT_BLOCK_LABEL}
          </div>
          <h3
            data-testid="commitment-block-title"
            className="truncate text-sm font-semibold text-text-primary"
          >
            {String(props.title || "") || (
              <span className="text-text-tertiary">
                Commitment title...
              </span>
            )}
          </h3>
          <p
            data-testid="commitment-block-due"
            className="mt-1 text-xs text-text-secondary"
          >
            {due}
          </p>
        </div>
        {!actions.readOnly && (
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => actions.onEdit?.(block.id)}
              className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2.5 py-1 text-xs text-text-primary hover:bg-recessed"
            >
              <Pencil size={13} />
              Edit
            </button>
            <button
              type="button"
              onClick={() => actions.onDuplicate?.(block.id)}
              className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2.5 py-1 text-xs text-text-primary hover:bg-recessed"
            >
              <Copy size={13} />
              Duplicate
            </button>
            <button
              type="button"
              onClick={() => actions.onDelete?.(block.id)}
              className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2.5 py-1 text-xs text-ember-ink hover:bg-recessed"
            >
              <Trash2 size={13} />
              Delete
            </button>
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <Chip>{category}</Chip>
        <Chip>{priority}</Chip>
        <Chip>{complexity}</Chip>
        {tagLabels.length > 0 && (
          <span title={tagLabels.join(", ")} className="inline-flex items-center gap-1">
            <Chip>{tagLabels[0]}</Chip>
            {tagLabels.length > 1 && <Chip>+{tagLabels.length - 1}</Chip>}
          </span>
        )}
        {regulatory && <Chip variant="regulatory">Regulatory</Chip>}
        <Chip>Dependencies: {dependencyCount}</Chip>
      </div>
      <div ref={contentRef} className="sr-only" />
    </section>
  );
}


export const commitmentBlockSpec = createReactBlockSpec(
  {
    type: COMMITMENT_BLOCK_TYPE,
    propSchema: commitmentBlockPropSchema,
    content: "inline" as const,
  },
  {
    render: ({ block, contentRef }) => {
      return (
        <CommitmentBlockView block={block} contentRef={contentRef} />
      );
    },
  },
);


export type CommitmentBlockSpec = typeof commitmentBlockSpec;


function Chip({
  children,
  variant = "neutral",
}: {
  children: React.ReactNode;
  variant?: "neutral" | "regulatory";
}) {
  return (
    <span
      className={
        variant === "regulatory"
          ? "rounded bg-[#F5DCC8] px-2 py-0.5 text-[#9A4B22]"
          : "rounded bg-recessed px-2 py-0.5 text-text-secondary"
      }
    >
      {children}
    </span>
  );
}


function optionLabel(value: string, options: Option[] = []): string {
  return options.find((option) => option.id === value || option.slug === value)
    ?.display_name ?? humanize(value);
}


function priorityLabel(value: string): string {
  if (value === "high") return "High";
  if (value === "medium") return "Medium";
  return "Low";
}


function complexityLabel(value: string): string {
  if (value === "5") return "XL";
  if (value === "3") return "L";
  if (value === "2") return "M";
  return "S";
}


function stringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value !== "string") return [];
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}


function plural(count: number, singular: string): string {
  return count === 1 ? singular : `${singular}s`;
}


function humanize(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}
